import { useActiveOrganizationGuard, useSession } from '@repo/api-client/react';
import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { BootSpinner } from './AuthGate';

/**
 * Wraps the entire authenticated app shell.
 *
 * Responsibilities:
 *   1. Run `useActiveOrganizationGuard` so the persisted active org
 *      is reconciled against the live list (drops stale ids without
 *      ever wiping during a transient probe).
 *   2. Bounce unverified emails to the verify-required screen.
 *   3. If the user has no businesses, push them through onboarding
 *      so they create their first one before landing on `/invoices`
 *      with empty state.
 *
 * Mounted INSIDE `<AuthGate>` so we know the user is authenticated.
 */
export function AppGuard({ children }: { children: ReactNode }) {
  const { emailVerified, organizations, organizationsLoaded } = useSession();
  const location = useLocation();
  useActiveOrganizationGuard();

  if (emailVerified === false && !location.pathname.startsWith('/verify-required')) {
    return <Navigate to="/verify-required" replace />;
  }

  // If the user has no orgs yet, gently push them through the
  // onboarding flow that ends in create-business. We allow the
  // settings/businesses screens through so the empty state is
  // recoverable. Wait until orgs are authoritatively loaded so a
  // transient `[]` doesn't bounce them on cold load.
  const onOnboarding =
    location.pathname.startsWith('/onboarding') ||
    location.pathname.startsWith('/settings/businesses') ||
    location.pathname.startsWith('/verify-required');
  if (organizationsLoaded && organizations.length === 0 && !onOnboarding) {
    return <Navigate to="/onboarding/create-business" replace />;
  }

  if (!organizationsLoaded && organizations.length === 0) {
    return <BootSpinner />;
  }

  return <>{children}</>;
}
